import type { ReactNode } from 'react';
import { Select } from '@base-ui/react/select';
import type { IGroupedOptions, ISelectionOption } from '@components/Pages/AnalyticsPage/analytics.types';
import { ProfessionChip } from '@components/ui/ProfessionChip/ProfessionChip';
import chevronDownSvg from '@assets/analytics/chevron-down.svg?raw';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './GroupedSelect.module.css';

interface IGroupedSelectProps {
  value: string | null;
  /** `null` only ever fires when `clearable` and the clear item is chosen. */
  onChange: (next: string | null) => void;
  groups: IGroupedOptions[];
  placeholder: string;
  ariaLabel: string;
  disabled?: boolean;
  /** Optional id forwarded to the trigger so a `<label htmlFor>` can bind it. */
  id?: string;
  /** When set, a leading "clear" item resets the selection to `null`. */
  clearable?: boolean;
  /**
   * Set when the select is rendered inside a modal dialog: lifts the portaled
   * popup above the dialog layer (--z-dialog-popover), which otherwise hides it.
   */
  elevated?: boolean;
  /** Label for the clear item (defaults to the placeholder). */
  clearLabel?: string;
  /** Custom render for a popup option (defaults to chip-or-text). */
  renderItem?: (opt: ISelectionOption) => ReactNode;
  /** Custom render for the selected value in the trigger (defaults to label). */
  renderValue?: (opt: ISelectionOption) => ReactNode;
}

export function GroupedSelect({
  value,
  onChange,
  groups,
  placeholder,
  ariaLabel,
  disabled,
  id,
  clearable,
  clearLabel,
  elevated,
  renderItem,
  renderValue,
}: IGroupedSelectProps) {
  const selected = groups.flatMap((g) => g.options).find((o) => o.id === value) ?? null;
  return (
    <Select.Root
      value={value ?? ''}
      onValueChange={(next: string | null) => { if (next === null && !clearable) return; onChange(next); }}
      disabled={disabled}
    >
      <Select.Trigger id={id} aria-label={ariaLabel} className={styles.trigger}>
        <Select.Value className={styles.value}>
          {selected
            ? (renderValue ? renderValue(selected) : selected.label)
            : <span className={styles.placeholder}>{placeholder}</span>}
        </Select.Value>
        <Select.Icon className={styles.icon}>
          <span aria-hidden="true" dangerouslySetInnerHTML={{ __html: chevronDownSvg }} />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Positioner
          className={elevated ? `${styles.positioner} ${styles.positionerInDialog}` : styles.positioner}
          sideOffset={4}
          alignItemWithTrigger={false}
        >
          <Select.Popup className={styles.popup}>
            <ScrollArea>
              {clearable && selected && (
                <Select.Item value={null} className={styles.clearItem} aria-label={clearLabel ?? placeholder}>
                  <Select.ItemText>{clearLabel ?? placeholder}</Select.ItemText>
                </Select.Item>
              )}
              {groups.map((group) => (
                <div key={group.groupId} className={styles.group} role="group" aria-label={group.groupLabel}>
                  <div className={styles.groupHeader}>{group.groupLabel}</div>
                  {group.options.map((opt) => (
                    // `Select.Item` needs an accessible name for testing-library
                    // role queries — when the visual is a chip the textContent
                    // alone wouldn't contain the contractor name, so `label`
                    // (the canonical formatted text) is mirrored to aria-label.
                    <Select.Item
                      key={opt.id}
                      value={opt.id}
                      className={styles.item}
                      aria-label={opt.label}
                    >
                      {renderItem ? (
                        renderItem(opt)
                      ) : opt.chip ? (
                        <ProfessionChip chip={opt.chip} />
                      ) : (
                        <Select.ItemText>{opt.label}</Select.ItemText>
                      )}
                    </Select.Item>
                  ))}
                </div>
              ))}
            </ScrollArea>
          </Select.Popup>
        </Select.Positioner>
      </Select.Portal>
    </Select.Root>
  );
}
